import { useAddress } from '@hooks/use-address';
import { useTransaction } from '@hooks/use-transaction';
import { type CoinStruct } from '@models/coins.vault.model';
import { walletConnectModel } from '@models/wallet-connect.model';
import { Transaction } from '@mysten/sui/transactions';
import Button from '@ui/button';
import { numericFormat, percentage } from '@utils/index';
import { scaleDown } from '@utils/math';
import { BN, Decimal } from 'parrot-sui-sdk';
import { FC, useMemo } from 'react';
import { cetus } from 'parrot-vaults-sdk';
import { OpenapiClientParrotVault } from 'src/openapi/parrot-vault';
import { SUI_CLOCK_OBJECT_ID } from '@mysten/sui/utils';
import { cetusClmmSDK } from '@libs/cetus.client';
import { config } from '@config';
import { adjustForCoinSlippage, ClmmPoolUtil, TickMath } from '@cetusprotocol/cetus-sui-clmm-sdk';
import { type QuoteResponse } from '@7kprotocol/sdk-ts';
import { type MyVaultsStruct } from '@models/myvault.model';
import { K7Aggregator } from '@libs/7k.provider';
import { globalSettingModel } from '@models/global-setting.model';
import { useComputed } from 'foca';

interface OwnProps {
  myVault: MyVaultsStruct | undefined;
  vault: OpenapiClientParrotVault.GetVaultVaultsResponse['result'][number];
  coinA: CoinStruct;
  coinB: CoinStruct;
  tabValue: number;
  amountA: string;
  amountB: string;
  availableBalanceA: string;
  availableBalanceB: string;
  closeDialog(): void;
  exactIn: boolean;
  aggregatorRoute: QuoteResponse | undefined;
  percent: number;
  handleClear(): void;
}
const WithdrawSubmit: FC<OwnProps> = ({
  amountA,
  amountB,
  tabValue,
  coinA,
  coinB,
  closeDialog,
  vault,
  aggregatorRoute,
  availableBalanceA,
  availableBalanceB,
  myVault,
  percent,
  handleClear,
}) => {
  const slippage = useComputed(globalSettingModel.liquiditySlippage);
  const address = useAddress();
  const transact = useTransaction();

  const deposit = async () => {
    if (!address) return;

    let final_amount_a = amountA;
    let final_amount_b = amountB;

    if (tabValue === 2 && aggregatorRoute) {
      final_amount_a = new Decimal(final_amount_a)
        .sub(aggregatorRoute.swapAmountWithDecimal)
        .toString();
      final_amount_b = aggregatorRoute.returnAmountWithDecimal;
    } else if (tabValue === 3 && aggregatorRoute) {
      final_amount_b = new Decimal(final_amount_b)
        .sub(aggregatorRoute.swapAmountWithDecimal)
        .toString();
      final_amount_a = aggregatorRoute.returnAmountWithDecimal;
    }

    let _amount_a = '0';
    let _amount_b = '0';
    let _title = <></>;

    if (tabValue === 2) {
      _amount_a = scaleDown(amountA, coinA.decimals).toString();
      _title = (
        <>
          Withdraw {numericFormat(_amount_a)} {coinA.symbol}
        </>
      );
    } else if (tabValue === 3) {
      _amount_b = scaleDown(amountB, coinB.decimals).toString();
      _title = (
        <>
          Withdraw {numericFormat(_amount_b)} {coinB.symbol}{' '}
        </>
      );
    } else {
      _amount_a = scaleDown(amountA, coinA.decimals).toString();
      _amount_b = scaleDown(amountB, coinB.decimals).toString();
      _title = (
        <>
          Withdraw {numericFormat(_amount_a)} {coinA.symbol} and {numericFormat(_amount_b)}{' '}
          {coinB.symbol}{' '}
        </>
      );
    }

    const ok = await transact({
      title: _title,
      successDescription: _title,
      fn: async () => {
        if (!myVault || !address) return;
        const _slippage = new Decimal(slippage).div(100).toNumber();

        let txb = new Transaction();
        const cetusPool = await cetusClmmSDK.Pool.getPool(vault.pool_id);
        const removeAmount = new Decimal(myVault.position_share).mul(percent).div(100).toFixed(0);

        const coinAmounts = ClmmPoolUtil.getCoinAmountFromLiquidity(
          new BN(removeAmount),
          new BN(cetusPool.current_sqrt_price),
          TickMath.tickIndexToSqrtPriceX64(vault.tick_lower),
          TickMath.tickIndexToSqrtPriceX64(vault.tick_upper),
          false,
        );

        const slippageTolerance = percentage(Number(slippage));
        const { tokenMaxA, tokenMaxB } = adjustForCoinSlippage(
          coinAmounts,
          slippageTolerance,
          false,
        );

        const [coinAReturn, coinBReturn] = cetus.remove(txb, [coinA.coin_type, coinB.coin_type], {
          vault: vault.vault_id,
          rewarderManager: config.rewarderManager,
          manager: config.vaultsManager,
          clmmConfig: cetusClmmSDK.sdkOptions.clmm_pool.config!.global_config_id,
          clmmPool: vault.pool_id,
          vaultPosition: txb.object(myVault.id),
          removeAmount: txb.pure.u64(removeAmount),
          amountACheck: txb.pure.u64(tokenMaxA.toString()),
          amountBCheck: txb.pure.u64(tokenMaxB.toString()),
          clock: SUI_CLOCK_OBJECT_ID,
        });

        if (tabValue === 2) {
          const { tx, coinOut } = await K7Aggregator.buildTransaction(
            aggregatorRoute!,
            _slippage.toString(),
            address,
            {
              tx: txb,
              coinIn: coinBReturn!,
            },
          );
          txb = tx;
          txb.transferObjects([coinAReturn!, coinOut!], address);
        } else if (tabValue === 3) {
          const { tx, coinOut } = await K7Aggregator.buildTransaction(
            aggregatorRoute!,
            _slippage.toString(),
            address,
            {
              tx: txb,
              coinIn: coinAReturn!,
            },
          );
          txb = tx;
          txb.transferObjects([coinBReturn!, coinOut!], address);
        } else {
          txb.transferObjects([coinAReturn!, coinBReturn!], address);
        }
        return txb;
      },
    });
    if (ok.status) {
      handleClear();
      percent === 100 && closeDialog();
    }
  };

  const { disabled, text, clickEvent } = useMemo(() => {
    let disabled = false;
    let text = 'Withdraw';
    let clickEvent = () => {};

    if (!address) {
      text = 'Connect Wallet';
      clickEvent = walletConnectModel.toggleDialog;
    } else if (
      (tabValue === 1 && (!amountA || !amountB)) ||
      (tabValue === 2 && !amountA) ||
      (tabValue === 3 && !amountB)
    ) {
      disabled = true;
    } else if (
      tabValue !== 3 &&
      (!availableBalanceA || new Decimal(amountA).gt(availableBalanceA))
    ) {
      text = `Insufficient liquidity`;
      disabled = true;
    } else if (
      tabValue !== 2 &&
      (!availableBalanceB || new Decimal(amountB).gt(availableBalanceB))
    ) {
      text = `Insufficient liquidity`;
      disabled = true;
    } else if ((tabValue === 2 || tabValue === 3) && !aggregatorRoute) {
      disabled = true;
    } else {
      clickEvent = deposit;
    }

    return {
      disabled,
      text,
      clickEvent,
    };
  }, [
    amountA,
    amountB,
    tabValue,
    address,
    coinA.symbol,
    coinB.symbol,
    walletConnectModel.toggleDialog,
    deposit,
    availableBalanceA,
    availableBalanceB,
  ]);

  return (
    <Button disabled={disabled} fullWidth onClick={clickEvent}>
      {text}
    </Button>
  );
};

export default WithdrawSubmit;
